import { Card, CardContent, CardHeader, Grid } from '@material-ui/core';
import { StyledComponentProps, withStyles } from '@material-ui/core/styles';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { IEntity } from '../../main/entities';
import { transl } from '../../main/transl';
import { Momentino } from '../../services/Momentino';
import { EmptyListDialog, setEmptyListDialogStore } from '../ordering/EmptyListDialog';

interface IOrder extends IEntity {
	creationTimestamp: string
}

interface ISettings {
	name: string
	show: 'orders' | 'nPositions' | 'nItems'
	colors: Array<string>
}

interface ICompProps extends ISettings {
	orders: Array<IOrder>
}

function yearmonthOfOrder(order) : string {
	return Momentino.timestampTo.yearmonth(order.creationTimestamp);
}

function monthRange(list: Array<string>) {
	const result: Array<string> = [];
	if (!list.length) return result;
	list = list.slice().sort();
	const min = list[0];
	const max = list[list.length-1];
	for (let s=min; s<=max; ) {
		result.push(s);
		let n = +s;
		n += n%100 == 12 ? 100-12+1 : 1;
		s = '' + n;
	}
	return result;
}

const styles = theme => ({
	root: {
		...theme.mixins.gutters(),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		display: 'flex',
		justifyContent: 'space-between',
		width: '100%',
	},
	card: {
		flex: 1,
	},
	cardHeader: {
	},
	cardContent: {
	},
});

class ByMonth extends React.Component<ICompProps> {
	_delta = e => {
		switch (this.props.show[0]) {
		case 'orders': return 1;
		case 'nPositions': return e.nPositions;
		case 'nItems': return e.nItems;
		}
	}

	@computed get _map() {
		const result = new Map();
		monthRange(this.props.orders.map(yearmonthOfOrder)).forEach(m => result.set(m, 0));
		this.props.orders.forEach(e => {
			const m = yearmonthOfOrder(e);
			result.set(m, result.get(m) + this._delta(e));
		});
		return result;
	}

	render() {
		const {colors} = this.props;
		const keys = [...this._map.keys()].sort();
		const labels = keys.map(k => moment(k, 'YYMM').format('MMM \'YY'));
		// const data = keys.map(k => ({t: moment(k, 'YYMM'), y: byMonth.get(k)}))
		const data = keys.map(k => this._map.get(k));
		const chartData = {
			labels,
			datasets: [
				{
					label: transl('orderchart.ByMonth'),
					backgroundColor: colors[0],
					data,
				},
			]
		};
		const chartOptions = {
			legend: {
				display: false
			},
			scales: {
				yAxes: [{
					ticks: {
						beginAtZero: true,
					}
				}],
			},
		};
		return <Bar data={chartData} options={chartOptions}/>;
	}
}

@observer class ByDayofweek extends React.Component<ICompProps> {
	@computed get _map() {
		const result = new Map()
		; ['Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.', 'So.'].forEach(d => result.set(d, 0));
		this.props.orders.forEach(e => {
			const d = Momentino.timestampTo.dayofweek(e.creationTimestamp);
			result.set(d, result.get(d) + 1);
		});
		return result;
	}

	render() {
		const keys = [...this._map.keys()];
		const labels = keys;
		const data = keys.map(k => this._map.get(k));
		const chartData = {
			labels,
			datasets: [
				{
					label: transl('orderchart.ByDayofweek'),
					// backgroundColor: ["#0074D9", "#FF4136", "#2ECC40", "#FF851B", "#7FDBFF", "#B10DC9", "#FFDC00", "#001f3f", "#39CCCC", "#01FF70", "#85144b", "#F012BE", "#3D9970", "#111111", "#AAAAAA"],
					backgroundColor: ['#5DA5DA', '#FAA43A', '#60BD68', '#F17CB0', '#B276B2', '#DECF3F', '#F15854'],
					data,
				},
			]
		};

		const chartOptions = {
		};
		return <Doughnut data={chartData} options={chartOptions}/>;
	}
}

interface IDashboardBodyProps {
	orders: Array<IOrder>
}

@observer class _DashboardBody extends React.Component<IDashboardBodyProps & StyledComponentProps> {
	render() {
		const classes = this.props.classes || {};
		const {orders} = this.props;
		const charts: Array<ISettings & {Comp: React.ElementType<ICompProps>}> = [
			{
				name: 'OrdersByMonth',
				Comp: ByMonth,
				show: ['orders'] as any,
				colors: ['#0000CC'],
			},
			{
				name: 'PositionsByMonth',
				Comp: ByMonth,
				show: ['nPositions'] as any,
				colors: ['#6600AA'],
			},
			{
				name: 'ItemsByMonth',
				Comp: ByMonth,
				show: ['nItems'] as any,
				colors: ['#990099'],
			},
			{
				name: 'ByDayofweek',
				Comp: ByDayofweek,
				show: [] as any,
				colors: [],
			},
		];
		return <>
			<Grid container spacing={8} alignItems="stretch" className={classes.root}>
				{
					charts.map(({name, Comp, ...args}) => <Grid key={name} item xs={12} md={6} lg={4}>
						<Card className={classes.card}>
							<CardHeader className={classes.cardHeader} title={transl('chart.' + name)}/>
							<CardContent className={classes.cardContent}><Comp orders={orders} name={name} {...args}/></CardContent>
						</Card>
					</Grid>)
				}
			</Grid>
			<EmptyListDialog store={setEmptyListDialogStore}/>
		</>;
	}
}

export const DashboardBody = withStyles(styles)(_DashboardBody);
